import { getShapeKey, UpdatePinDispatch } from '@lessonup/pin-renderer';
import { newShapePinComponent, PinComponent, ShapeType } from '@lessonup/pins-shared';
import {
  Button,
  IconArrowRight,
  IconRocket,
  IconShapeCircle,
  IconShapeRectangle,
  IconShapeStar,
  IconShapeTriangle,
  SpaceBetween,
  spacing,
} from '@lessonup/ui-components';
import React from 'react';
import { useFeatureFlags } from '../../../../../utils/featureFlags/useFeatureFlags';
import { SetModeSelecting } from '../../../context/EditorContext/EditorContext.types';
import { useActivePin } from '../../../context/EditorContext/hooks/useEditorState';
import { useEditorTranslation } from '../../../hooks/useEditorTranslation';
import { createAddPinComponent } from '../../../utils/pin/pin.utils';

export interface ShapePickerProps {
  close: () => void;
  dispatch: UpdatePinDispatch;
  setModeSelecting: SetModeSelecting;
  handleOnComplete?: (createdPinComponent: PinComponent) => void;
  shapePickerHandler?: (shapeType: ShapeType) => void;
}

const SHAPES: Array<{ type: ShapeType; icon: React.JSX.Element }> = [
  { type: 'RECTANGLE', icon: <IconShapeRectangle /> },
  { type: 'TRIANGLE', icon: <IconShapeTriangle /> },
  { type: 'ELLIPSE', icon: <IconShapeCircle /> },
  { type: 'STAR', icon: <IconShapeStar /> },
];

const featureFlaggedShapes: Array<{ type: ShapeType; icon: React.JSX.Element }> = [
  { type: 'POLYGON', icon: <IconShapeTriangle /> },
  { type: 'ARROW', icon: <IconArrowRight /> },
  { type: 'ICON', icon: <IconRocket /> },
];

export const ShapePicker: React.FC<ShapePickerProps> = ({
  dispatch,
  setModeSelecting,
  handleOnComplete,
  shapePickerHandler,
}) => {
  const pin = useActivePin();
  const { t } = useEditorTranslation();
  const { isFeatureEnabled } = useFeatureFlags();
  const addPinComponent = createAddPinComponent(dispatch, setModeSelecting, pin);

  let shapesToRender = SHAPES;
  if (isFeatureEnabled('editorShapeAllShapes')) {
    shapesToRender = [...SHAPES, ...featureFlaggedShapes];
  }

  return (
    <SpaceBetween direction="x" spacing={spacing.size0} padding={spacing.size8}>
      {shapesToRender.map((shape) => (
        <Button
          key={shape.type}
          aria-label={t(`toolbelt.shapePicker.${getShapeKey(shape.type)}`)}
          buttonType="neutral"
          size="large"
          buttonShape="rect"
          iconStart={shape.icon}
          onClick={
            shapePickerHandler
              ? () => shapePickerHandler(shape.type)
              : () =>
                  addPinComponent({
                    newComponent: (position) => newShapePinComponent({ position, shapeType: shape.type }),
                    onComplete: (createdPinComponent) => handleOnComplete?.(createdPinComponent),
                  })
          }
        />
      ))}
    </SpaceBetween>
  );
};
